import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import {
  EAllowStatus,
  ExtendedIDTargetingType,
  ICountryGroup,
  ResourceActive,
  TargetBoolType,
  TargetingCCPA,
  TargetingCoppa,
  TargetingLogic,
} from '~/src/dash/api/console-api';
import { transformCountryGroup } from '~/src/dash/api/country-group';
import {
  parseArray,
  parseFloat,
  parseNullString,
  parseNumber,
  parseString,
  transformModel,
} from '~/src/dash/api/resource-transforms';
import {
  addDomainAndTokenToUrl,
  IHttpResponse,
  restDelete,
  restGet,
  restPatch,
  restPost,
} from '~/src/dash/api/rest';
import { EBool } from '~/src/dash/api/types/bool';
import { ILocalTargetingParameter } from '~/src/dash/components/local-targeting/local-targeting';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { filterSitesByActive } from '~/src/dash/utils/models';
import {
  obfuscateDestinations,
  obfuscateInventoryRouteName,
  obfuscateSiteName,
} from '~/src/dash/utils/obfuscate';

export type RoutingCondition = number;
export const RoutingConditionUnknown: RoutingCondition = 0;
export const RoutingConditionTargetingParams: RoutingCondition = 1;
export const RoutingConditionPercentageRequests: RoutingCondition = 2;

export type DestinationType = number;
export const DestinationTypeUnknown: DestinationType = 0;
export const DestinationTypeVastURL: DestinationType = 1;
export const DestinationTypeSite: DestinationType = 2;
export const DestinationTypeRoute: DestinationType = 3;

export type TargetingLivestream = number;
export const TargetingLivestreamAll: TargetingLivestream = 0;

export type DestinationTargetFieldKeys =
  | 'TargetingAppBundleID'
  | 'TargetingContentChannel'
  | 'TargetingContentDistributor'
  | 'TargetingContentGenre'
  | 'TargetingContentID'
  | 'TargetingContentNetwork'
  | 'TargetingContentProducer'
  | 'TargetingContentSeries'
  | 'TargetingContentTitle'
  | 'TargetingDealID'
  | 'TargetingDeviceTypes'
  | 'TargetingDeviceSubTypes'
  | 'TargetingDeviceModels'
  | 'TargetingCustom4'
  | 'TargetingCustom5'
  | 'TargetingCustom6'
  | 'TargetingCustom7'
  | 'TargetingCustom8'
  | 'TargetingCustom9'
  | 'TargetingCustom10'
  | 'TargetingCustom11'
  | 'TargetingCustom12'
  | 'TargetingCustom13'
  | 'TargetingCustom14'
  | 'TargetingCustom15'
  | 'TargetingCustom16'
  | 'TargetingCustom17'
  | 'TargetingCustom18'
  | 'TargetingWSeat'
  | 'TargetingFWSectionIDs'
  | 'LocalTargeting'
  | 'TargetingDeviceCountry'
  | 'TargetingUID20TargetingType'
  | 'TargetingGooglePairIDTargetingType'
  | 'TargetingLiveRampATSTargetingType'
  | 'TargetingIrisSignalTargetingType';
// | 'TargetingSamsungIdentityTargetingType'
// | 'TargetingSamsungEPGMetadataTargetingType';

export type DestinationInclusionFieldKeys =
  | 'TargetingAppBundleIDIsAllowed'
  | 'TargetingContentChannelIsAllowed'
  | 'TargetingContentDistributorIsAllowed'
  | 'TargetingContentGenreIsAllowed'
  | 'TargetingContentIDIsAllowed'
  | 'TargetingContentNetworkIsAllowed'
  | 'TargetingContentProducerIsAllowed'
  | 'TargetingContentRatingIsAllowed'
  | 'TargetingContentSeriesIsAllowed'
  | 'TargetingContentTitleIsAllowed'
  | 'TargetingDeviceTypesIsAllowed'
  | 'TargetingDevicesIsAllowed'
  | 'TargetingDeviceSubTypesIsAllowed'
  | 'TargetingDeviceModelsIsAllowed'
  | 'TargetingCustom4Inclusion'
  | 'TargetingCustom5Inclusion'
  | 'TargetingCustom6Inclusion'
  | 'TargetingCustom7Inclusion'
  | 'TargetingCustom8Inclusion'
  | 'TargetingCustom9Inclusion'
  | 'TargetingCustom10Inclusion'
  | 'TargetingCustom11Inclusion'
  | 'TargetingCustom12Inclusion'
  | 'TargetingCustom13Inclusion'
  | 'TargetingCustom14Inclusion'
  | 'TargetingCustom15Inclusion'
  | 'TargetingCustom16Inclusion'
  | 'TargetingCustom17Inclusion'
  | 'TargetingCustom18Inclusion'
  | 'TargetingFWSectionIDsInclusion'
  | 'TargetingCountryGroupIDsIsAllowed'
  | 'LocalTargetingIsAllowed';

export type DestinationMenuLists =
  | 'targetingCountriesList'
  | 'targetingCountryGroupList'
  | 'inventoryRouteChannelList'
  | 'inventoryRouteRouteList';
export type DestinationLocalTargetingParams = 'localTargetingParameters';

export interface IDestination {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  Name: string;
  DestinationType: DestinationType;
  BackfillEnabled: EBool;
  RouteID: number;
  VastURL: string;
  SiteID?: number;
  ForwardingRouteID?: number;
  SiteUUID: string;
  ShareOfRequests: number;
  TargetingAppBundleID: string;
  TargetingAppBundleIDIsAllowed: EAllowStatus;
  TargetingContentChannel: string;
  TargetingContentChannelIsAllowed: EAllowStatus;
  TargetingContentDistributor: string;
  TargetingContentDistributorIsAllowed: EAllowStatus;
  TargetingContentGenre: string;
  TargetingContentGenreIsAllowed: EAllowStatus;
  TargetingContentID: string;
  TargetingContentIDIsAllowed: EAllowStatus;
  TargetingContentNetwork: string;
  TargetingContentNetworkIsAllowed: EAllowStatus;
  TargetingContentProducer: string;
  TargetingContentProducerIsAllowed: EAllowStatus;
  TargetingContentRating: string;
  TargetingContentRatingIsAllowed: EAllowStatus;
  TargetingContentSeries: string;
  TargetingContentSeriesIsAllowed: EAllowStatus;
  TargetingContentTitle: string;
  TargetingContentTitleIsAllowed: EAllowStatus;
  TargetingDealID: string;
  TargetingDeviceTypes: string;
  TargetingDeviceTypesIsAllowed: EAllowStatus;
  TargetingDeviceSubTypes: string;
  TargetingDeviceSubTypesIsAllowed: EAllowStatus;
  TargetingDeviceModels: string;
  TargetingDeviceModelsIsAllowed: EAllowStatus;
  TargetingDevices: string;
  TargetingDevicesIsAllowed: EAllowStatus;
  TargetingCustom4: string;
  TargetingCustom4Inclusion: EAllowStatus;
  TargetingCustom5: string;
  TargetingCustom5Inclusion: EAllowStatus;
  TargetingCustom6: string;
  TargetingCustom6Inclusion: EAllowStatus;
  TargetingCustom7: string;
  TargetingCustom7Inclusion: EAllowStatus;
  TargetingCustom8: string;
  TargetingCustom8Inclusion: EAllowStatus;
  TargetingCustom9: string;
  TargetingCustom9Inclusion: EAllowStatus;
  TargetingCustom10: string;
  TargetingCustom10Inclusion: EAllowStatus;
  TargetingCustom11: string;
  TargetingCustom11Inclusion: EAllowStatus;
  TargetingCustom12: string;
  TargetingCustom12Inclusion: EAllowStatus;
  TargetingCustom13: string;
  TargetingCustom13Inclusion: EAllowStatus;
  TargetingCustom14: string;
  TargetingCustom14Inclusion: EAllowStatus;
  TargetingCustom15: string;
  TargetingCustom15Inclusion: EAllowStatus;
  TargetingCustom16: string;
  TargetingCustom16Inclusion: EAllowStatus;
  TargetingCustom17: string;
  TargetingCustom17Inclusion: EAllowStatus;
  TargetingCustom18: string;
  TargetingCustom18Inclusion: EAllowStatus;
  TargetingLivestream: TargetingLivestream;
  TargetingWSeat: string;
  TargetingFWSectionIDs: string;
  TargetingFWSectionIDsInclusion: EAllowStatus;
  CountryGroups: ICountryGroup[];
  TargetingCountryGroupIDs: string;
  TargetingCountryGroupIDsIsAllowed: EAllowStatus;
  LocalTargeting: string;
  LocalTargetingIsAllowed: EAllowStatus;
  TargetingDeviceCountry: string;
  TargetingDeviceCountryIsAllowed: EAllowStatus;
  TargetingCoppa: TargetingCoppa;
  TargetingLimitedAdTracking: TargetBoolType;
  targetingCountriesList: IMenuList;
  targetingCountryGroupList: IMenuList;
  localTargetingParameters: ILocalTargetingParameter[];
  inventoryRouteChannelList: IMenuList;
  inventoryRouteRouteList: IMenuList;
  LocalTargetingLogic: TargetingLogic;
  TargetingCCPA: TargetingCCPA;
  // ExtendedIDTargetingType
  TargetingUID20TargetingType: ExtendedIDTargetingType;
  TargetingGooglePairIDTargetingType: ExtendedIDTargetingType;
  TargetingLiveRampATSTargetingType: ExtendedIDTargetingType;
  TargetingIrisSignalTargetingType: ExtendedIDTargetingType;
  // TargetingSamsungIdentityTargetingType: ExtendedIDTargetingType;
  // TargetingSamsungEPGMetadataTargetingType: ExtendedIDTargetingType;
}

export const extendedIDTargetingTypeKeys: string[] = [
  'TargetingUID20TargetingType',
  'TargetingGooglePairIDTargetingType',
  'TargetingLiveRampATSTargetingType',
  'TargetingIrisSignalTargetingType',
  // 'TargetingSamsungIdentityTargetingType',
  // 'TargetingSamsungEPGMetadataTargetingType',
];

export const transformDestination = transformModel<IDestination>({
  ID: parseNumber(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  Name: parseString(['Name']),
  DestinationType: parseNumber(['DestinationType']),
  BackfillEnabled: parseNumber(['BackfillEnabled']),
  RouteID: parseNumber(['RouteID']),
  VastURL: parseString(['VastURL']),
  SiteID: parseNumber(['SiteID']),
  ForwardingRouteID: parseNumber(['ForwardingRouteID']),
  SiteUUID: parseString(['SiteUUID']),
  ShareOfRequests: parseNumber(['ShareOfRequests']),
  TargetingAppBundleID: parseString(['TargetingAppBundleID']),
  TargetingAppBundleIDIsAllowed: parseNumber(['TargetingAppBundleIDIsAllowed']),
  TargetingContentChannel: parseString(['TargetingContentChannel']),
  TargetingContentChannelIsAllowed: parseNumber(['TargetingContentChannelIsAllowed']),
  TargetingContentDistributor: parseString(['TargetingContentDistributor']),
  TargetingContentDistributorIsAllowed: parseNumber(['TargetingContentDistributorIsAllowed']),
  TargetingContentGenre: parseString(['TargetingContentGenre']),
  TargetingContentGenreIsAllowed: parseNumber(['TargetingContentGenreIsAllowed']),
  TargetingContentID: parseString(['TargetingContentID']),
  TargetingContentIDIsAllowed: parseNumber(['TargetingContentIDIsAllowed']),
  TargetingContentNetwork: parseString(['TargetingContentNetwork']),
  TargetingContentNetworkIsAllowed: parseNumber(['TargetingContentNetworkIsAllowed']),
  TargetingContentProducer: parseString(['TargetingContentProducer']),
  TargetingContentProducerIsAllowed: parseNumber(['TargetingContentProducerIsAllowed']),
  TargetingContentRating: parseString(['TargetingContentRating']),
  TargetingContentRatingIsAllowed: parseNumber(['TargetingContentRatingIsAllowed']),
  TargetingContentSeries: parseString(['TargetingContentSeries']),
  TargetingContentSeriesIsAllowed: parseNumber(['TargetingContentSeriesIsAllowed']),
  TargetingContentTitle: parseString(['TargetingContentTitle']),
  TargetingContentTitleIsAllowed: parseNumber(['TargetingContentTitleIsAllowed']),
  TargetingDealID: parseString(['TargetingDealID']),
  TargetingDeviceTypes: parseString(['TargetingDeviceTypes']),
  TargetingDeviceTypesIsAllowed: parseNumber(['TargetingDeviceTypesIsAllowed']),
  TargetingDeviceSubTypes: parseString(['TargetingDeviceSubTypes']),
  TargetingDeviceSubTypesIsAllowed: parseNumber(['TargetingDeviceSubTypesIsAllowed']),
  TargetingDeviceModels: parseString(['TargetingDeviceModels']),
  TargetingDeviceModelsIsAllowed: parseNumber(['TargetingDeviceModelsIsAllowed']),
  TargetingDevices: parseString(['TargetingDevices']),
  TargetingDevicesIsAllowed: parseNumber(['TargetingDevicesIsAllowed']),
  TargetingCustom4: parseString(['TargetingCustom4']),
  TargetingCustom4Inclusion: parseNumber(['TargetingCustom4Inclusion']),
  TargetingCustom5: parseString(['TargetingCustom5']),
  TargetingCustom5Inclusion: parseNumber(['TargetingCustom5Inclusion']),
  TargetingCustom6: parseString(['TargetingCustom6']),
  TargetingCustom6Inclusion: parseNumber(['TargetingCustom6Inclusion']),
  TargetingCustom7: parseString(['TargetingCustom7']),
  TargetingCustom7Inclusion: parseNumber(['TargetingCustom7Inclusion']),
  TargetingCustom8: parseString(['TargetingCustom8']),
  TargetingCustom8Inclusion: parseNumber(['TargetingCustom8Inclusion']),
  TargetingCustom9: parseString(['TargetingCustom9']),
  TargetingCustom9Inclusion: parseNumber(['TargetingCustom9Inclusion']),
  TargetingCustom10: parseString(['TargetingCustom10']),
  TargetingCustom10Inclusion: parseNumber(['TargetingCustom10Inclusion']),
  TargetingCustom11: parseString(['TargetingCustom11']),
  TargetingCustom11Inclusion: parseNumber(['TargetingCustom11Inclusion']),
  TargetingCustom12: parseString(['TargetingCustom12']),
  TargetingCustom12Inclusion: parseNumber(['TargetingCustom12Inclusion']),
  TargetingCustom13: parseString(['TargetingCustom13']),
  TargetingCustom13Inclusion: parseNumber(['TargetingCustom13Inclusion']),
  TargetingCustom14: parseString(['TargetingCustom14']),
  TargetingCustom14Inclusion: parseNumber(['TargetingCustom14Inclusion']),
  TargetingCustom15: parseString(['TargetingCustom15']),
  TargetingCustom15Inclusion: parseNumber(['TargetingCustom15Inclusion']),
  TargetingCustom16: parseString(['TargetingCustom16']),
  TargetingCustom16Inclusion: parseNumber(['TargetingCustom16Inclusion']),
  TargetingCustom17: parseString(['TargetingCustom17']),
  TargetingCustom17Inclusion: parseNumber(['TargetingCustom17Inclusion']),
  TargetingCustom18: parseString(['TargetingCustom18']),
  TargetingCustom18Inclusion: parseNumber(['TargetingCustom18Inclusion']),
  TargetingLivestream: parseNumber('TargetingLivestream'),
  TargetingWSeat: parseString(['TargetingWSeat']),
  TargetingFWSectionIDs: parseString(['TargetingFWSectionIDs']),
  TargetingFWSectionIDsInclusion: parseNumber(['TargetingFWSectionIDsInclusion']),
  CountryGroups: parseArray(['CountryGroups'], transformCountryGroup),
  TargetingCountryGroupIDs: parseString(['TargetingCountryGroupIDs']),
  TargetingCountryGroupIDsIsAllowed: parseNumber(['TargetingCountryGroupIDsIsAllowed']),
  LocalTargeting: parseString(['LocalTargeting']),
  LocalTargetingIsAllowed: parseNumber(['LocalTargetingIsAllowed']),
  TargetingDeviceCountry: parseString(['TargetingDeviceCountry']),
  TargetingDeviceCountryIsAllowed: parseNumber(['TargetingDeviceCountryIsAllowed']),
  TargetingCoppa: parseNumber(['TargetingCoppa']),
  TargetingLimitedAdTracking: parseNumber(['TargetingLimitedAdTracking']),
  LocalTargetingLogic: parseNumber(['LocalTargetingLogic']),
  TargetingCCPA: parseNumber(['TargetingCCPA']),
  TargetingUID20TargetingType: parseNumber(['TargetingUID20TargetingType']),
  TargetingGooglePairIDTargetingType: parseNumber(['TargetingGooglePairIDTargetingType']),
  TargetingLiveRampATSTargetingType: parseNumber(['TargetingLiveRampATSTargetingType']),
  TargetingIrisSignalTargetingType: parseNumber(['TargetingIrisSignalTargetingType']),
  // TargetingSamsungIdentityTargetingType: parseNumber(['TargetingSamsungIdentityTargetingType']),
  // TargetingSamsungEPGMetadataTargetingType: parseNumber([
  //   'TargetingSamsungEPGMetadataTargetingType',
  // ]),
});

export interface IInventoryRoute {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  EndpointURL: string;
  Name: string;
  Active: ResourceActive;
  RoutingCondition: RoutingCondition;
  PublisherID: number;
  PublisherUUID: string;
  Destinations: IDestination[];
  Region: string;
  EndpointURLParams: string;
  FreewheelPublisherID: number | null;
  Timeout: number;
  SamplingPercentageDisabledUntil: string;
  SamplingPercentage: number;
}

export type IInventoryRouteDesinationKey = 'Destinations';

export const transformInventoryRoute = transformModel<IInventoryRoute>({
  ID: parseNumber(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  EndpointURL: parseString(['EndpointURL']),
  Name: parseString(['Name']),
  Active: parseNumber(['Active']),
  RoutingCondition: parseNumber(['RoutingCondition']),
  PublisherID: parseNumber(['PublisherID']),
  PublisherUUID: parseString(['PublisherUUID']),
  Destinations: parseArray(['Destinations'], transformDestination),
  Region: parseString(['Region']),
  EndpointURLParams: parseString(['EndpointURLParams']),
  FreewheelPublisherID: parseNullString(['FreewheelPublisherID']),
  Timeout: parseNumber(['Timeout']),
  SamplingPercentageDisabledUntil: parseString(['SamplingPercentageDisabledUntil']),
  SamplingPercentage: parseFloat(['SamplingPercentage']),
});

export function destinationDemandSourceTypeToString(source_type: DestinationType) {
  switch (source_type) {
    case DestinationTypeSite:
      return 'Channel';
    case DestinationTypeVastURL:
      return 'VAST';
    case DestinationTypeRoute:
      return 'Route';
    default:
      break;
  }
  return 'unknown';
}

export function inventoryRouteConditionTypeToString(source_type: RoutingCondition) {
  switch (source_type) {
    case RoutingConditionTargetingParams:
      return 'Targeting';
    case RoutingConditionPercentageRequests:
      return '% of Requests';
    default:
      break;
  }
  return 'unknown';
}

export async function getInventoryRoutesByPublisherID(
  publisherID: string,
): Promise<IInventoryRoute[]> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/routes`);
  return await restGet<any>(reqUrl, {}).then((val: IHttpResponse<any>) => {
    const Routes = [] as IInventoryRoute[];
    if (val.parsedBody) {
      for (const Route of val.parsedBody) {
        const parsedRoute = transformInventoryRoute(Route);
        Routes.push(parsedRoute);
      }
    }
    return Routes;
  });
}

export async function getInventoryRoutesAsIMenuList(
  session: ISessionState,
  user: IUserState,
): Promise<Record<string, IMenuList>> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${session.publisher.ID}/routes`);
  return await restGet<any>(reqUrl, {})
    .then((val: IHttpResponse<any>) => {
      const Routes = val.parsedBody?.map(transformInventoryRoute);
      return { inventory_routes: generateInventoryRouteMenuList(Routes, [], user.user.Username) };
    })
    .catch((e) => {
      console.error(e);
      return {
        inventory_routes: {},
      };
    });
}

export async function getInventoryRouteByID(
  publisherID: string,
  routeID: string,
): Promise<IInventoryRoute> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/routes/${routeID}`);
  return await restGet<any>(reqUrl, {}).then((val: IHttpResponse<any>) => {
    return transformInventoryRoute(val.parsedBody);
  });
}

export async function updateInventoryRoute(
  route: IInventoryRoute,
  publisherID: string,
): Promise<IHttpResponse<IInventoryRoute>> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/routes/${route.ID}`);
  return await restPatch<IInventoryRoute>(reqUrl, route);
}

export async function createInventoryRoute(
  route: IInventoryRoute,
  publisherID: string,
): Promise<IHttpResponse<IInventoryRoute>> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/routes`);
  return await restPost<IInventoryRoute>(reqUrl, route);
}

export async function deleteInventoryRoute(
  routeID: string,
  publisherID: string,
): Promise<IHttpResponse<IInventoryRoute>> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/routes/${routeID}`);
  return await restDelete<IInventoryRoute>(reqUrl, undefined);
}

export const generateInventoryRouteMenuList = (
  inventoryRoutes: IInventoryRoute[],
  selectedIDs: string[],
  username: string,
): IMenuList => {
  const menuList: IMenuList = {};

  inventoryRoutes.forEach((inventoryRoute: IInventoryRoute) => {
    menuList[inventoryRoute.ID] = {
      ID: inventoryRoute.ID.toString(),
      selected: selectedIDs.includes(inventoryRoute.ID.toString()),
      title: obfuscateInventoryRouteName(
        inventoryRoute,
        username,
        `[${inventoryRoute.ID}] ${inventoryRoute.Name}`,
      ),
    };
  });

  return menuList;
};

export const generateDestinationMenuList = (
  destinations: IDestination[],
  selectedIDs: string[],
  defaultSelectAll: boolean,
  username: string,
): IMenuList => {
  const list = {} as IMenuList;

  destinations.forEach((destination: IDestination) => {
    list[destination.ID] = {
      title: obfuscateDestinations(
        destination,
        username,
        `[${destination.ID}] ${destination.Name}`,
      ),
      ID: destination.ID.toString(),
      // default select all destinations to start
      selected: defaultSelectAll ? true : selectedIDs.includes(destination.ID.toString()),
    } as IListItem;
  });
  return list;
};

export const genInvenRouteChannelListFromSelected = (
  siteID = 0,
  sites: ISmallSite[],
  username: string,
) => {
  const activeSites = filterSitesByActive(sites);
  const inventoryRouteChannelList = {} as IMenuList;
  for (const site of activeSites) {
    inventoryRouteChannelList[site.ID] = {
      title: obfuscateSiteName(site, username, `[${site.ID}] ${site.Name}`),
      selected: site.ID === siteID,
      ID: site.ID.toString(),
      uuid: site.UUID,
    };
  }

  return inventoryRouteChannelList;
};

export const genInvenRouteRouteListFromSelected = (
  routeID = 0,
  routes: IInventoryRoute[],
  username: string,
) => {
  const activeRoutes = filterInventoryRoutesByActive(routes);
  const inventoryRouteRouteList = {} as IMenuList;
  for (const route of activeRoutes) {
    inventoryRouteRouteList[route.ID] = {
      title: obfuscateInventoryRouteName(route, username, `[${route.ID}] ${route.Name}`),
      selected: route.ID === routeID,
      ID: route.ID.toString(),
    };
  }

  return inventoryRouteRouteList;
};

export const filterInventoryRoutesByActive = (routes: IInventoryRoute[]) => {
  return routes.filter((route: IInventoryRoute) => {
    return route.Active === 2;
  });
};
